import { isKey } from 'types/predicates';

/**
 * maps content with the proper locale
 *
 * @param contentMap - a content object with per locale strings / JSX.Elements
 * @param translationKey - key of the locale to display
 * @param [fallbackTranslationKey='US'] - default key in case the content doesn't exist on the desired locale
 *
 * @returns t - translation mapping function
 */

type I18nFlatContentMap<C> = {
  US: C;
  CA?: Partial<C>;
  GB?: Partial<C>;
};

const isComplete = <T>(obj: T | Partial<T>, path: PropertyKey): obj is T => {
  return obj && isKey(obj, path) && obj[path] !== undefined;
};

/**
 * maps content with the proper locale
 *
 * @param contentMap - a content object with per locale strings / JSX.Elements
 * @param translationKey - key of the locale to display
 * @param [fallbackTranslationKey='US'] - default key in case the content doesn't exist on the desired locale
 *
 * @returns t - translation mapping function
 */
export function createFlatTranslationFn<C extends object>(
  contentMap: I18nFlatContentMap<C>,
  translationKey: keyof I18nFlatContentMap<C>,
  fallbackTranslationKey: keyof I18nFlatContentMap<C> = 'US',
) {
  return <P extends keyof C>(path: P): C[P] => {
    const currNode = contentMap[translationKey];
    const defaultCurrNode = contentMap[fallbackTranslationKey];

    if (currNode && isComplete(currNode, path)) {
      return currNode[path];
    }
    if (defaultCurrNode && isComplete(defaultCurrNode, path)) {
      return defaultCurrNode?.[path];
    }
    throw new Error('No translation node available');
  };
}
